import moment, { Moment } from 'moment';

import {
  DayOfWeek,
  Language,
  MemberContactTimeType,
  MemberEngagementLevel,
  RiskLevel,
  States,
} from '@vestahealthcare/common/enums';
import {
  BHIMember,
  CCMMember,
  CCMMemberDetail,
  MemberWarningDetail,
  PodReportingCCM,
  PodReportingOverview,
  PodReportingRPM,
  PodReportingSummary,
  PodReportingTOC,
  PodReportingVisit,
  RPMMember,
} from '@vestahealthcare/common/models';
import { MemberWarningType } from '@vestahealthcare/common/models/member-warnings/MemberWarningType';
import { DATE_FORMAT } from '@vestahealthcare/common/utils/constants';

import Api, {
  PaginatedResponseWithTime,
  PaginationParams,
} from 'dash/src/services/Api';

import { VestaTeamFilters } from './types';

export type GetCCMMemberParams = PaginationParams &
  VestaTeamFilters & {
    aideScheduleDay?: DayOfWeek[];
    aideScheduleTime?: MemberContactTimeType[];
    bhiMinutesFrom?: number;
    bhiMinutesTo?: number;
    biannualAppointmentStatus?: string;
    biannualStatus?: string;
    brandId?: string[];
    carePlanDateFrom?: Moment;
    carePlanDateTo?: Moment;
    carePlanDue?: boolean;
    careTeamDiscoveryDue?: boolean;
    ccmCallStatus?: string[];
    contactDay?: DayOfWeek[];
    contactTime?: MemberContactTimeType[];
    engagementLevel?: MemberEngagementLevel[];
    excludedMemberIds?: number[];
    excludedProgramExtensionId?: number[];
    excludedReferralSource?: string[];
    hasCarePlan?: boolean;
    hasAideScheduleInfo?: boolean;
    hasContactInfo?: boolean;
    hasLanguage?: boolean;
    hasProfessionalTime?: boolean;
    hasProfessionalTimeOrTouchedByNp?: boolean;
    hasProgramExtensions?: boolean;
    hasRiskLevel?: boolean;
    hasRpmAdherence?: boolean;
    hasValidCCM?: boolean;
    isBiannualComplete?: boolean;
    isComplete?: boolean;
    initialCPDue?: boolean;
    isUnreachable?: string;
    insurance?: string[];
    language?: Language[];
    memberIds?: number[];
    memberName?: string;
    numberOfCalls?: number;
    programExtensionFilter?: string[];
    referralSource?: string[];
    riskLevel?: RiskLevel[];
    recentCyclesMetCount?: number[];
    rpmAdherenceId?: string[];
    rpmMinutesFrom?: number;
    rpmMinutesTo?: number;
    statusId?: string[];
    lastReadingDateFrom?: Moment;
    lastReadingDateTo?: Moment;
    rpmStartDateFrom?: Moment;
    rpmStartDateTo?: Moment;
    startDateFrom?: Moment;
    startDateTo?: Moment;
    state?: States[];
    touchedByNp?: boolean;
    totalMinutesFrom?: number;
    totalMinutesTo?: number;
    touchPointReason?: string[];
    validMinutesFrom?: number;
    validMinutesTo?: number;
    vitalCollectionMethod?: string[];
    warningTypeId?: string[];
    worklistGroupId?: number[];
  };

const prepareGetParams = ({
  aideScheduleDay,
  aideScheduleTime,
  careCoordinatorId,
  carePlanDateFrom,
  carePlanDateTo,
  contactDay,
  contactTime,
  engagementLevel,
  engagementOwnerId,
  excludedMemberIds,
  healthCoachId,
  language,
  npOwnerId,
  programExtensionFilter,
  riskLevel,
  lastReadingDateFrom,
  lastReadingDateTo,
  rpmStartDateFrom,
  rpmStartDateTo,
  state,
  startDateFrom,
  startDateTo,
  ...params
}: GetCCMMemberParams) => ({
  aideScheduleDay: aideScheduleDay?.length
    ? aideScheduleDay?.map((item) => item?.valueOf())
    : undefined,
  aideScheduleTime: aideScheduleTime?.length
    ? aideScheduleTime?.map((item) => item?.valueOf())
    : undefined,
  careCoordinatorId: careCoordinatorId?.length ? careCoordinatorId : undefined,
  carePlanDateFrom: carePlanDateFrom?.format(DATE_FORMAT),
  carePlanDateTo: carePlanDateTo?.format(DATE_FORMAT),
  contactDay: contactDay?.length
    ? contactDay?.map((item) => item?.valueOf())
    : undefined,
  contactTime: contactTime?.length
    ? contactTime?.map((item) => item?.valueOf())
    : undefined,
  engagementLevel: engagementLevel?.length
    ? engagementLevel?.map((item) => item?.valueOf())
    : undefined,
  engagementOwnerId: engagementOwnerId?.length ? engagementOwnerId : undefined,
  excludedMemberIds: excludedMemberIds?.length ? excludedMemberIds : undefined,
  healthCoachId: healthCoachId?.length ? healthCoachId : undefined,
  language: language?.length
    ? language?.map((item) => item?.valueOf())
    : undefined,
  month: moment().startOf('month').format(DATE_FORMAT),
  npOwnerId: npOwnerId?.length ? npOwnerId : undefined,
  programExtensionFilter: programExtensionFilter?.length
    ? programExtensionFilter
    : undefined,
  riskLevel: riskLevel?.length
    ? riskLevel?.map((item) => item?.valueOf())
    : undefined,
  lastReadingDateFrom: lastReadingDateFrom?.format(DATE_FORMAT),
  lastReadingDateTo: lastReadingDateTo?.format(DATE_FORMAT),
  rpmStartDateFrom: rpmStartDateFrom?.format(DATE_FORMAT),
  rpmStartDateTo: rpmStartDateTo?.format(DATE_FORMAT),
  state: state?.length ? state?.map((item) => item?.valueOf()) : undefined,
  startDateFrom: startDateFrom?.format(DATE_FORMAT),
  startDateTo: startDateTo?.format(DATE_FORMAT),
  ...params,
});

export const fecthCCMMembers = async (
  params: GetCCMMemberParams,
): Promise<PaginatedResponseWithTime<CCMMember>> => {
  const {
    ccmdashboardMembers: { items, pagination, lastUpdatedAt },
  } = await Api.getv2('/ccm-dashboard', prepareGetParams(params));
  return {
    items: items.map((x: any) => new CCMMember(x)),
    lastUpdatedAt: lastUpdatedAt ? moment.unix(lastUpdatedAt) : undefined,
    pagination,
  };
};

export const fecthRPMMembers = async (
  params: GetCCMMemberParams,
): Promise<PaginatedResponseWithTime<RPMMember>> => {
  const {
    rpmDashboardMembers: { items, pagination, lastUpdatedAt },
  } = await Api.getv2('/rpm-dashboard', prepareGetParams(params));
  return {
    items: items.map((x: any) => new RPMMember(x)),
    lastUpdatedAt: lastUpdatedAt ? moment.unix(lastUpdatedAt) : undefined,
    pagination,
  };
};

export const fecthBHIMembers = async (
  params: GetCCMMemberParams,
): Promise<PaginatedResponseWithTime<BHIMember>> => {
  const {
    bhiDashboardMembers: { items, pagination, lastUpdatedAt },
  } = await Api.getv2('/bhi-dashboard', prepareGetParams(params));
  return {
    items: items.map((x: any) => new BHIMember(x)),
    lastUpdatedAt: lastUpdatedAt ? moment.unix(lastUpdatedAt) : undefined,
    pagination,
  };
};

export const fecthCCMTouchpointReasons = async (): Promise<string[]> => {
  const { touchpointReasons } = await Api.getv2(
    '/ccm-dashboard/touchpoint-reasons',
  );
  return touchpointReasons as string[];
};

export const fecthCompleteTouchpointReasons = async (): Promise<string[]> => {
  const { touchpointReasons } = await Api.getv2(
    '/ccm-dashboard/complete-touchpoint-reasons',
  );
  return touchpointReasons as string[];
};

export const fecthNPAppointmentTypes = async (): Promise<string[]> => {
  const { appointmentTypes } = await Api.getv2(
    '/ccm-dashboard/np-appointment-types',
  );
  return appointmentTypes as string[];
};

export type PatchAssignToWorklistParams = {
  worklistGroupId: number | null;
  includedMemberIds?: number[];
  filter?: GetCCMMemberParams;
};

export const assignToGroupWorklist = async ({
  filter,
  ...params
}: PatchAssignToWorklistParams) => {
  await Api.patchv2JSON('/ccm-dashboard/actions/worklist-bulk', {
    filter: filter ? prepareGetParams(filter) : undefined,
    month: moment().startOf('month').format(DATE_FORMAT),
    ...params,
  });
};

export const getMemberInfoData = async (id: number) => {
  const { ccmdashboardDetail } = await Api.getv2(
    `/ccm-dashboard/${id}/detail`,
    {
      month: moment().startOf('month').format(DATE_FORMAT),
    },
  );

  const memberDetail = new CCMMemberDetail(ccmdashboardDetail);
  // fixme should be moved to BE [VESTA-4375]
  memberDetail.warnings = memberDetail.warnings.filter(
    (item: MemberWarningDetail) => {
      return (
        item.type.id !== MemberWarningType.DUE_NP_BIANNUAL &&
        item.type.id !== MemberWarningType.DUE_CARE_PLAN_UPDATE
      );
    },
  );
  return memberDetail;
};

export const downloadCCMDashboard = async (params: GetCCMMemberParams) => {
  const blob: Blob = await Api.getv2Blob(
    '/ccm-dashboard/export',
    prepareGetParams(params),
  );
  const csvURL = window.URL.createObjectURL(blob);
  const tempLink = document.createElement('a');
  tempLink.href = csvURL;
  tempLink.setAttribute('download', 'ccm_member_export.csv');
  tempLink.click();
};

export const downloadRPMDashboard = async (params: GetCCMMemberParams) => {
  const blob: Blob = await Api.getv2Blob(
    '/rpm-dashboard/export',
    prepareGetParams(params),
  );
  const csvURL = window.URL.createObjectURL(blob);
  const tempLink = document.createElement('a');
  tempLink.href = csvURL;
  tempLink.setAttribute('download', 'rpm_member_export.csv');
  tempLink.click();
};

export type GetPodReportDataParams = Omit<
  VestaTeamFilters,
  'engagementOwnerId'
> & {
  engagementLevel?: string[];
  excludedProgramExtensionId?: number[];
  isComplex?: boolean;
  isUnreachable?: string;
  language?: string[];
  month?: Moment;
  programExtensionFilter?: string[];
  referralSource?: string[];
  status?: string[];
  vitalCollectionMethod?: string[];
  warningTypeId?: string;
  worklistGroupId?: number[];
};

const preparePodParams = ({
  month,
  npOwnerId,
  worklistGroupId,
  ...rest
}: GetPodReportDataParams) => ({
  month: month?.format(DATE_FORMAT),
  npOwnerId: npOwnerId?.filter((id) => id >= 0),
  worklistGroupId: worklistGroupId?.filter((id) => id >= 0),
  ...rest,
});

export const fetchPodReportingSummary = async (
  params: GetPodReportDataParams,
): Promise<PodReportingSummary> => {
  const { podReportingSummary } = await Api.getv2(
    '/ccm-dashboard/pod-reporting/summary',
    preparePodParams(params),
  );
  const summary = new PodReportingSummary(podReportingSummary);

  summary.perDay = Math.round(summary.perDay);
  summary.perDayGoal = Math.round(summary.perDayGoal);

  return summary;
};

export const fetchPodReportingCCMSummary = async (
  params: GetPodReportDataParams,
): Promise<PodReportingSummary> => {
  const { podReportingSummary } = await Api.getv2(
    '/ccm-dashboard/pod-reporting/ccm-summary',
    preparePodParams(params),
  );
  const summary = new PodReportingSummary(podReportingSummary);

  summary.perDay = Math.round(summary.perDay);
  summary.perDayGoal = Math.round(summary.perDayGoal);

  return summary;
};

export const fetchCCMPodReportingRPMSummary = async (
  params: GetPodReportDataParams,
): Promise<PodReportingSummary> => {
  const { podReportingSummary } = await Api.getv2(
    '/ccm-dashboard/pod-reporting/rpm-summary',
    preparePodParams(params),
  );
  const summary = new PodReportingSummary(podReportingSummary);

  summary.perDay = Math.round(summary.perDay);
  summary.perDayGoal = Math.round(summary.perDayGoal);

  return summary;
};

export const fetchCCMPodReportingGroups = async (
  params: GetPodReportDataParams,
): Promise<PodReportingSummary> => {
  const { podReportingSummary } = await Api.getv2(
    '/ccm-dashboard/pod-reporting/ccm-summary',
    preparePodParams(params),
  );
  const summary = new PodReportingSummary(podReportingSummary);

  summary.perDay = Math.round(summary.perDay);
  summary.perDayGoal = Math.round(summary.perDayGoal);

  return summary;
};

export const exportCCMPodReportingGroups = async (
  params: GetPodReportDataParams,
): Promise<void> => {
  const blob: Blob = await Api.getv2Blob(
    '/ccm-dashboard/pod-reporting/by-groups/export',
    preparePodParams(params),
  );
  const csvURL = window.URL.createObjectURL(blob);
  const tempLink = document.createElement('a');
  tempLink.href = csvURL;
  tempLink.setAttribute('download', 'pod_reporting_summary.csv');
  tempLink.click();
};

export const fetchPodReportingOverview = async (
  params: GetPodReportDataParams,
): Promise<PodReportingOverview> => {
  const { overview } = await Api.getv2(
    '/ccm-dashboard/pod-reporting/overview',
    preparePodParams(params),
  );
  return new PodReportingOverview(overview);
};

export const fetchPodReportingCCM = async (
  params: GetPodReportDataParams,
): Promise<PodReportingCCM> => {
  const { ccm } = await Api.getv2('/ccm-dashboard/pod-reporting/ccm', {
    ...preparePodParams(params),
  });
  return new PodReportingCCM(ccm);
};

export const fetchPodReportingRPM = async (
  params: GetPodReportDataParams,
): Promise<PodReportingRPM> => {
  const { rpm } = await Api.getv2('/ccm-dashboard/pod-reporting/rpm', {
    ...preparePodParams(params),
  });
  return new PodReportingRPM(rpm);
};

export const fetchPodReportingTOC = async (
  params: GetPodReportDataParams,
): Promise<PodReportingTOC> => {
  const { toc } = await Api.getv2('/ccm-dashboard/pod-reporting/toc', {
    ...preparePodParams(params),
  });
  return new PodReportingTOC(toc);
};

export const fetchPodReportingVisits = async (
  params: GetPodReportDataParams,
): Promise<PodReportingVisit> => {
  const { visitSummary } = await Api.getv2(
    '/ccm-dashboard/pod-reporting/visit-summary',
    {
      ...preparePodParams(params),
    },
  );
  return new PodReportingVisit(visitSummary);
};
